/**
 * NetworkDeployFlow — the single-pane deploy UX (NETWORK-REGISTRY.md §14). Replaces
 * the one-shot DeployPackageWizard with a thin abstraction over three INDEPENDENT
 * working operations: network creation, connector/MCP config, and room creation.
 *
 * Phases:
 *   1. ``intro``         — a dedicated network briefing (no room here): what deploying
 *      creates (N agents / N skills) and what it needs to work. Confirm → deploy with
 *      ``createRoom:false`` → a bare ACTIVE network lands immediately.
 *   2. ``network-tools`` — (only if the package has network-scope connectors/MCPs)
 *      the shared NetworkToolConfigPanel bound at network scope. Partial save allowed.
 *   3. ``rooms``         — create a room via the EXISTING ReleaseNetworkDialog ("Create
 *      a Room"; workspace auto-defaulted), or finish. Picking "Create a room" hands the
 *      screen fully to that dialog — its own exit (created OR cancelled) ends this flow;
 *      we never return to the rooms prompt.
 *
 * Everything downstream (connectors, rooms, room-scope tools) is also reachable later
 * from the network tile's ⋮ menu — this flow is just the guided first pass. Nothing here
 * is invented: deploy(createRoom:false), the tool panel, and ReleaseNetworkDialog exist.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { deployRegistryPackage } from '@stepflow/ui-shared/services/networkRegistryApi.js';
import { getRegistryPackageDetail } from '@stepflow/ui-shared/services/networkRegistryApi.js';
import NetworkToolConfigPanel from './NetworkToolConfigPanel.jsx';
import ReleaseNetworkDialog from './ReleaseNetworkDialog.jsx';

function countConfigurable(toolsUsed, scope) {
  const set = new Set();
  for (const t of toolsUsed || []) {
    if (!t || (t.kind !== 'connector' && t.kind !== 'mcp') || t.name === 'workspace') continue;
    const s = t.scope || 'network';
    const inScope = scope === 'network' ? s === 'network' : (s === 'pool' || s === 'agent');
    if (inScope) set.add(`${t.kind}|${t.name}|${s}|${t.agentId || ''}`);
  }
  return set.size;
}

export default function NetworkDeployFlow({
  open, pkg, tenantId = '', onClose, onNetworkReady,
  // Advisory duplicate detection (computed by the host from the in-memory network
  // list — no API call). Networks already deployed from THIS registry package.
  // Each: { networkId, name, stepflowVersion, version, sameVersion, _net }.
  existingDeployments = [],
  // Jump into "create a room" on an existing network instead of a second copy.
  onAddRoom,
}) {
  const registryNetworkId = String(pkg?.registryNetworkId || '');
  const version = Number(pkg?.version) || undefined;
  const stepflowVersion = pkg?.stepflowVersion || '';
  const title = pkg?.title || registryNetworkId;
  const agentCount = Number(pkg?.agentCount) || 0;
  const skillCount = Number(pkg?.skillCount) || 0;

  // Split the matches: an EXACT-version copy (the strong nudge — a room is the
  // better, lower-maintenance instance) vs a different-version deployment (purely
  // informational — that's a defensible parallel-version situation → point at Upgrade).
  const dupSame = useMemo(
    () => (existingDeployments || []).filter((d) => d && d.sameVersion),
    [existingDeployments],
  );
  const dupOther = useMemo(
    () => (existingDeployments || []).filter((d) => d && !d.sameVersion),
    [existingDeployments],
  );

  const [phase, setPhase] = useState('intro'); // intro | network-tools | rooms
  const [name, setName] = useState(pkg?.title || '');
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState(null);

  const [toolsUsed, setToolsUsed] = useState(null);   // fetched at deploy time
  const [deployed, setDeployed] = useState(null);     // { networkId, network }
  const [releaseOpen, setReleaseOpen] = useState(false);

  // A short, human briefing of what this network needs — connectors + MCPs by name,
  // deduped, workspace excluded (it's baked in at room creation).
  const needsSummary = useMemo(() => {
    const names = [];
    const seen = new Set();
    for (const t of toolsUsed || []) {
      if (!t || (t.kind !== 'connector' && t.kind !== 'mcp') || t.name === 'workspace') continue;
      if (seen.has(t.name)) continue;
      seen.add(t.name);
      names.push(t.name);
    }
    return names;
  }, [toolsUsed]);

  // Prefetch toolsUsed on open so the intro can tell the user WHAT the network needs
  // to work (connectors + MCPs by name) before they commit. Best-effort.
  useEffect(() => {
    if (!open || !registryNetworkId) return undefined;
    let alive = true;
    getRegistryPackageDetail(registryNetworkId, version)
      .then((d) => { if (alive) setToolsUsed(Array.isArray(d?.toolsUsed) ? d.toolsUsed : []); })
      .catch(() => { if (alive) setToolsUsed([]); });
    return () => { alive = false; };
  }, [open, registryNetworkId, version]);

  const reset = useCallback(() => {
    setPhase('intro'); setName(pkg?.title || ''); setDeploying(false); setError(null);
    setToolsUsed(null); setDeployed(null); setReleaseOpen(false);
  }, [pkg]);

  const close = useCallback(() => { if (!deploying) { reset(); onClose?.(); } }, [deploying, reset, onClose]);

  // Confirm on the intro → deploy the BARE network (no room), then branch to the
  // network-tools phase (if any) or straight to rooms.
  const confirmDeploy = useCallback(async () => {
    setDeploying(true);
    setError(null);
    try {
      const body = { createRoom: false };
      if (version) body.version = version;
      if (name.trim()) body.name = name.trim();
      const res = await deployRegistryPackage(registryNetworkId, body);
      const nid = res?.networkId;
      if (!nid) throw new Error('Deploy returned no network id.');
      setDeployed({ networkId: nid, network: res?.network || null });
      onNetworkReady?.(res);   // let the host refresh its network list immediately
      // toolsUsed was prefetched on open; branch to network-tools if any exist.
      const tu = Array.isArray(toolsUsed) ? toolsUsed : [];
      setPhase(countConfigurable(tu, 'network') > 0 ? 'network-tools' : 'rooms');
    } catch (e) {
      setError(e?.message || 'Deploy failed');
    } finally {
      setDeploying(false);
    }
  }, [registryNetworkId, version, name, onNetworkReady, toolsUsed]);

  // A room was created via ReleaseNetworkDialog → just refresh the host's network
  // list (the room count changed). We do NOT branch back into this flow: once the
  // user picked "Create a room" the release dialog OWNS the screen and its own exit
  // (close/success) ends the whole deploy flow — see closeAfterRoom below. Room-scope
  // connectors remain configurable later from the network tile's ⋮ menu.
  const onRoomReleased = useCallback(() => {
    onNetworkReady?.({ networkId: deployed?.networkId });   // refresh (room count changed)
  }, [deployed, onNetworkReady]);

  // The release dialog's SINGLE terminal exit — fires whether a room was created
  // (after onReleased) or the user cancelled. Either way the deploy flow ends; we
  // never return to the "rooms" prompt.
  const closeAfterRoom = useCallback(() => { reset(); onClose?.(); }, [reset, onClose]);

  if (!open) return null;

  const nid = deployed?.networkId;

  return (
    <AnimatePresence>
      {/* While the "Create a Room" dialog is open it OWNS the screen — hide this
          flow's own backdrop + card so the two modals don't stack (the deploy-flow
          card was showing behind the release dialog). The ReleaseNetworkDialog is
          still rendered below; closing it returns to this flow's rooms phase. */}
      {!releaseOpen ? (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.button
          type="button" aria-label="Close"
          className="absolute inset-0 bg-black/40"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
        />
        <motion.div
          role="dialog" aria-modal="true" aria-labelledby="deployflow-title"
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
          className="relative z-[101] flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
        >
          {/* ---------- Phase: intro (dedicated network page) ---------- */}
          {phase === 'intro' ? (
            <div data-testid="deployflow-intro">
              <h2 id="deployflow-title" className="text-lg font-semibold text-gray-900">Deploy “{title}”</h2>
              <p className="mt-1 text-sm text-gray-600">
                This creates your own copy of the network — a private, editable instance in your workspace.
              </p>

              <dl className="mt-4 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Agents</dt>
                  <dd className="font-medium text-gray-900">{agentCount || '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Skills</dt>
                  <dd className="font-medium text-gray-900">{skillCount || '—'}</dd>
                </div>
              </dl>

              {needsSummary.length ? (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-[13px] text-amber-900"
                  data-testid="deployflow-needs">
                  To work, this network needs: <span className="font-medium">{needsSummary.join(', ')}</span>.
                  You’ll connect these next — or later, from the network’s menu.
                </div>
              ) : null}

              {/* Advisory (never blocking): you already run this exact version. A ROOM
                  is the better instance — a fully separate runtime (own roster, config
                  pins, workspace, history) frozen at this release, and one network is
                  less to maintain than a second identical copy. Deploy stays available. */}
              {dupSame.length ? (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2.5 text-[13px] text-blue-900"
                  data-testid="deployflow-dup-same">
                  <p>
                    You already run{' '}
                    <span className="font-medium">
                      {dupSame.map((d) => d.name).slice(0, 2).join(', ')}
                      {dupSame.length > 2 ? ` +${dupSame.length - 2} more` : ''}
                    </span>{' '}
                    from this package at{' '}
                    <span className="font-medium">{stepflowVersion || 'this version'}</span>.
                  </p>
                  <p className="mt-1 text-blue-800">
                    To run another instance, add a <span className="font-medium">room</span> to it
                    instead — a fully separate instance with its own config and history, frozen at
                    this release, and easier to maintain than a second copy. Or deploy a new,
                    independent network below.
                  </p>
                  {onAddRoom ? (
                    <button type="button"
                      onClick={() => onAddRoom(dupSame[0]?._net || dupSame[0])}
                      disabled={deploying}
                      data-testid="deployflow-add-room-instead"
                      className="mt-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                      Add a room to “{dupSame[0]?.name}” instead
                    </button>
                  ) : null}
                </div>
              ) : null}

              {/* Different-version deployment already exists — informational only. The
                  right way to move to a newer version is Upgrade (a new release on the
                  existing network), not a parallel copy; but we don't block either. */}
              {!dupSame.length && dupOther.length ? (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[13px] text-gray-600"
                  data-testid="deployflow-dup-other">
                  You already run{' '}
                  <span className="font-medium">{dupOther[0]?.name}</span> from this package
                  {dupOther[0]?.stepflowVersion ? <> (on {dupOther[0].stepflowVersion})</> : null}
                  {stepflowVersion ? <>; you’re deploying {stepflowVersion}</> : null}. To move an
                  existing network to this version, use <span className="font-medium">Upgrade</span> from
                  its menu instead of a parallel copy.
                </div>
              ) : null}

              <p className="mt-4 text-[13px] leading-relaxed text-gray-600">
                The network deploys on its own — no room yet. Once it’s created you can
                connect the services it needs, then spin up one or more rooms to run it.
                Everything here can also be done later from the network’s menu.
              </p>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700" htmlFor="deployflow-name">
                  Network name <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input id="deployflow-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder={title} autoComplete="off" disabled={deploying} />
              </div>

              {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
              <div className="mt-6 flex items-center justify-between">
                <button type="button" onClick={close} disabled={deploying}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40">
                  Cancel
                </button>
                <button type="button" onClick={confirmDeploy} disabled={deploying}
                  data-testid="deployflow-confirm"
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                  {deploying ? 'Deploying…' : 'Deploy network'}
                </button>
              </div>
            </div>
          ) : null}

          {/* ---------- Phase: network-scope tools ---------- */}
          {phase === 'network-tools' && nid ? (
            <div className="flex min-h-0 flex-1 flex-col" data-testid="deployflow-network-tools">
              <h2 className="text-lg font-semibold text-gray-900">Connect “{title}”</h2>
              <p className="mt-1 text-sm text-gray-600">
                Network-level connections. You can configure them now or later.
              </p>
              <div className="mt-3 min-h-0 flex-1">
                <NetworkToolConfigPanel
                  pkg={pkg} toolsUsed={toolsUsed} networkId={nid} scope="network"
                  tenantId={tenantId} onDone={() => setPhase('rooms')}
                />
              </div>
            </div>
          ) : null}

          {/* ---------- Phase: rooms ---------- */}
          {phase === 'rooms' && nid ? (
            <div data-testid="deployflow-rooms">
              <h2 className="text-lg font-semibold text-gray-900">Rooms for “{title}”</h2>
              <p className="mt-1 text-sm text-gray-600">
                A room is a running instance of the network. Create one now, or finish and
                add rooms later from the network’s menu.
              </p>
              <div className="mt-5 flex items-center justify-between">
                <button type="button" onClick={() => { reset(); onClose?.(); }}
                  data-testid="deployflow-finish"
                  className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
                  Finish
                </button>
                <button type="button" onClick={() => setReleaseOpen(true)}
                  data-testid="deployflow-create-room"
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                  + Create a room
                </button>
              </div>
            </div>
          ) : null}
        </motion.div>
      </div>
      ) : null}

      {/* The EXISTING "Create a Room" dialog — a SIBLING of this flow's modal (not
          nested), rendered ALONE while open so there's no double-modal stack.
          Its own onClose returns to the rooms phase card above. Workspace
          auto-defaults (bootstrapWorkspace). */}
      {releaseOpen && nid ? (
        <ReleaseNetworkDialog
          open
          networkId={nid}
          networkName={name.trim() || title}
          network={deployed?.network || undefined}
          onClose={closeAfterRoom}
          onReleased={onRoomReleased}
        />
      ) : null}
    </AnimatePresence>
  );
}
